import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Camera, Crop, Upload, PenLine, CheckCircle2, MessageCircle, Mic, Send, Loader2, X, Square,
  ChevronRight, FolderInput, Wrench, Rocket, BarChart3,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const MIME_EXT = { "audio/webm": "webm", "audio/mp4": "mp4", "audio/ogg": "ogg", "audio/mpeg": "mp3", "audio/wav": "wav" };
const pickMime = () => {
  const opts = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  if (typeof MediaRecorder === "undefined") return "";
  return opts.find((m) => MediaRecorder.isTypeSupported(m)) || "";
};

const SendTile = ({ icon: Icon, label, hint, tone, accent, onClick, disabled, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className={`group relative flex flex-col items-center justify-center gap-2.5 rounded-2xl border p-4 lg:p-5 text-center transition-all active:scale-95 min-h-[118px]
      ${disabled ? "border-white/[0.06] bg-white/[0.02] opacity-50 cursor-not-allowed"
                 : "border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.05] hover:-translate-y-0.5 hover:border-white/20"}`}>
    <span className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
      style={{ background: disabled ? "rgba(255,255,255,0.06)" : `${tone || accent}1F`, color: disabled ? "#9ca3af" : (tone || accent) }}>
      <Icon className="w-5 h-5" />
    </span>
    <span className="text-[13px] font-bold text-gray-100 leading-tight">{label}</span>
    {hint && <span className="text-[10.5px] text-gray-500 leading-tight">{hint}</span>}
  </button>
);

const Step = ({ icon: Icon, no, title, sub, tone, accent, last }) => (
  <div className="flex items-center gap-2 shrink-0">
    <div className="flex items-center gap-2.5">
      <span className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${tone || accent}1A`, color: tone || accent }}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="leading-tight">
        <div className="text-[12px] font-bold text-gray-100 whitespace-nowrap">{no}. {title}</div>
        <div className="text-[10.5px] text-gray-500 whitespace-nowrap">{sub}</div>
      </div>
    </div>
    {!last && <ChevronRight className="w-4 h-4 text-gray-600 shrink-0" />}
  </div>
);

export default function SmartIntake({ accent, clientId, onDone, onCheckApprovals, onReady }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [writing, setWriting] = useState(false);
  const [writeKind, setWriteKind] = useState("wunsch");
  const [text, setText] = useState("");
  const [full, setFull] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const fotoRef = useRef(null);
  const shotRef = useRef(null);
  const fileRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const cidQ = clientId ? `?client_id=${clientId}` : "";
  const OK = t("Eingegangen — KEWAG prüft Ihren Wunsch und meldet sich.");

  const loadFull = () => api.get(`/cockpit/files/storage${cidQ}`).then((r) => setFull(!!r.data?.over)).catch(() => {});
  useEffect(() => { loadFull(); /* eslint-disable-next-line */ }, [clientId]);

  const afterSend = () => { onDone?.(); loadFull(); };
  const resetInputs = () => { [fotoRef, shotRef, fileRef].forEach((r) => { if (r.current) r.current.value = ""; }); };

  const uploadFile = async (files, kind) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      await api.post(`/cockpit/files/intake${cidQ}`, fd);
      toast.success(OK);
      afterSend();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Senden fehlgeschlagen"));
    } finally { setBusy(false); resetInputs(); }
  };

  const openWriter = (kind) => { setWriteKind(kind); setWriting(true); };

  const sendText = async () => {
    const v = text.trim();
    if (!v) { toast.error(t("Bitte beschreiben Sie kurz Ihren Wunsch.")); return; }
    setBusy(true);
    try {
      await api.post(`/cockpit/files/intake/text${cidQ}`, { text: v, kind: writeKind });
      toast.success(OK);
      setText(""); setWriting(false); afterSend();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Senden fehlgeschlagen"));
    } finally { setBusy(false); }
  };

  // ---- Sprachaufnahme ----
  const stopStream = () => { if (streamRef.current) { streamRef.current.getTracks().forEach((tr) => tr.stop()); streamRef.current = null; } if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  const startRecording = async () => {
    if (full) { toast.error(t("Ihr Speicher ist voll.")); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error(t("Sprachaufnahme wird von diesem Gerät nicht unterstützt.")); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => uploadVoice();
      mediaRef.current = mr;
      mr.start();
      setRecSecs(0); setRecording(true);
      timerRef.current = setInterval(() => setRecSecs((s) => s + 1), 1000);
    } catch (e) {
      toast.error(t("Mikrofon-Zugriff verweigert. Bitte erlauben Sie den Zugriff."));
    }
  };
  const cancelRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") { mediaRef.current.onstop = null; mediaRef.current.stop(); }
    chunksRef.current = []; stopStream(); setRecording(false); setRecSecs(0);
  };
  const stopAndSend = () => { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); setRecording(false); stopStream(); };
  const uploadVoice = async () => {
    const chunks = chunksRef.current || [];
    if (!chunks.length) return;
    const mime = (mediaRef.current && mediaRef.current.mimeType) || "audio/webm";
    const base = mime.split(";")[0];
    const ext = MIME_EXT[base] || "webm";
    const blob = new Blob(chunks, { type: base });
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, `sprachnachricht.${ext}`);
      const r = await api.post(`/cockpit/files/intake/voice${cidQ}`, fd);
      const tr = (r.data?.transcript || "").trim();
      toast.success(tr ? t("Sprachnachricht eingegangen: ") + `„${tr.slice(0, 80)}“` : OK);
      afterSend();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Senden fehlgeschlagen"));
    } finally { setBusy(false); setRecSecs(0); chunksRef.current = []; }
  };
  useEffect(() => () => { stopStream(); }, []);

  // Aktionen fürs rechte «Schnellaktionen»-Panel bereitstellen
  useEffect(() => {
    onReady?.({
      foto: () => fotoRef.current?.click(),
      screenshot: () => shotRef.current?.click(),
      datei: () => fileRef.current?.click(),
      wunsch: () => openWriter("wunsch"),
      freigabe: () => onCheckApprovals?.(),
      frage: () => openWriter("frage"),
      voice: () => startRecording(),
    });
    /* eslint-disable-next-line */
  }, []);

  const mm = String(Math.floor(recSecs / 60)).padStart(2, "0");
  const ss = String(recSecs % 60).padStart(2, "0");

  return (
    <div className="rounded-3xl border p-5 lg:p-7 relative overflow-hidden"
      style={{ borderColor: `${accent}59`, boxShadow: `0 0 0 1px ${accent}22, 0 0 60px -22px ${accent}`, background: "linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.008))" }}
      data-testid="smart-intake">
      <h2 className="text-xl lg:text-2xl font-black tracking-tight text-center">{t("Was möchten Sie KEWAG senden?")}</h2>
      <p className="text-sm text-gray-400 text-center max-w-2xl mx-auto mt-2 mb-5">
        {t("Senden Sie einfach ein Foto, einen Screenshot, eine Datei oder einen Wunsch. KEWAGPage ordnet es für Sie ein und macht daraus den nächsten Schritt.")}
      </p>

      {full && (
        <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/[0.08] px-3.5 py-2.5 text-sm text-red-200" data-testid="smart-intake-full">
          {t("Ihr Speicher ist voll. Bitte geben Sie Dateien frei oder fragen Sie mehr Speicher an.")}
        </div>
      )}
      {busy && (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-300 mb-4" data-testid="smart-intake-busy">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: accent }} /> {t("Wird gesendet …")}
        </div>
      )}

      {recording ? (
        <div className="rounded-2xl border p-4 flex flex-col sm:flex-row items-center justify-between gap-3" style={{ borderColor: `${accent}55`, background: `${accent}0D` }} data-testid="smart-intake-recording">
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm font-bold text-gray-100">{t("Aufnahme läuft …")}</span>
            <span className="text-sm font-mono text-gray-300" data-testid="smart-intake-rec-timer">{mm}:{ss}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={cancelRecording} data-testid="smart-intake-rec-cancel"
              className="text-sm font-semibold rounded-xl px-3 py-2 border border-white/10 text-gray-300 hover:bg-white/10 transition-colors">{t("Abbrechen")}</button>
            <button onClick={stopAndSend} data-testid="smart-intake-rec-stop"
              className="flex items-center gap-2 text-sm font-bold rounded-xl px-4 py-2 text-black transition-transform active:scale-95" style={{ background: accent }}>
              <Square className="w-4 h-4" /> {t("Stopp & senden")}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <SendTile icon={Camera} label={t("Foto senden")} hint={t("Kamera öffnen")} tone={accent} accent={accent} disabled={busy || full}
            testid="smart-intake-foto" onClick={() => fotoRef.current?.click()} />
          <SendTile icon={Crop} label={t("Screenshot senden")} hint={t("Bildschirm erfassen")} tone="#60a5fa" accent={accent} disabled={busy || full}
            testid="smart-intake-screenshot" onClick={() => shotRef.current?.click()} />
          <SendTile icon={Upload} label={t("Datei hochladen")} hint={t("Datei auswählen")} tone="#a78bfa" accent={accent} disabled={busy || full}
            testid="smart-intake-datei" onClick={() => fileRef.current?.click()} />
          <SendTile icon={PenLine} label={t("Wunsch schreiben")} hint={t("Anliegen mitteilen")} tone="#f59e0b" accent={accent} disabled={busy}
            testid="smart-intake-wunsch" onClick={() => openWriter("wunsch")} />
          <SendTile icon={CheckCircle2} label={t("Freigabe prüfen")} hint={t("Offene Freigaben")} tone="#22c55e" accent={accent} disabled={busy}
            testid="smart-intake-freigabe" onClick={() => onCheckApprovals?.()} />
          <SendTile icon={MessageCircle} label={t("Frage an KEWAG")} hint={t("Direkt an uns")} tone="#38bdf8" accent={accent} disabled={busy}
            testid="smart-intake-frage" onClick={() => openWriter("frage")} />
        </div>
      )}

      {writing && !recording && (
        <div className="mt-4 rounded-2xl border border-white/10 bg-black/30 p-3.5" data-testid="smart-intake-writer">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400">{writeKind === "frage" ? t("Ihre Frage") : t("Ihr Wunsch")}</span>
            <button onClick={() => { setWriting(false); setText(""); }} data-testid="smart-intake-writer-close"
              className="p-1 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors"><X className="w-4 h-4" /></button>
          </div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="smart-intake-text"
            placeholder={writeKind === "frage" ? t("Ihre Frage in Kurzform — z. B. «Wie ändere ich meine Öffnungszeiten?».") : t("Ihr Wunsch in Kurzform — z. B. «Bitte neue Öffnungszeiten auf der Startseite».")}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-gray-200 focus:outline-none focus:border-[color:var(--client-accent)]/60 resize-none" />
          <div className="flex justify-end mt-2.5">
            <button onClick={sendText} disabled={busy || !text.trim()} data-testid="smart-intake-send"
              className="flex items-center gap-2 text-sm font-bold rounded-xl px-4 py-2.5 text-black disabled:opacity-50 transition-transform active:scale-95"
              style={{ background: accent }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("An KEWAG senden")}
            </button>
          </div>
        </div>
      )}

      {/* Prozess-Leiste */}
      <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="smart-intake-flow">
        <Step no="1" icon={Camera} title={t("Senden")} sub={t("Sie senden uns etwas")} tone="#60a5fa" accent={accent} />
        <Step no="2" icon={FolderInput} title={t("Einordnen")} sub={t("Wir prüfen & ordnen ein")} tone="#a78bfa" accent={accent} />
        <Step no="3" icon={Wrench} title={t("Aufgabe / Wunsch")} sub={t("Wir erstellen den nächsten Schritt")} tone="#f59e0b" accent={accent} />
        <Step no="4" icon={CheckCircle2} title={t("Freigabe")} sub={t("Sie prüfen und geben frei")} tone="#22c55e" accent={accent} />
        <Step no="5" icon={Rocket} title={t("Umsetzung")} sub={t("Wir setzen es um")} tone={accent} accent={accent} />
        <Step no="6" icon={BarChart3} title={t("Monatsreport")} sub={t("Sie sehen das Ergebnis")} tone="#38bdf8" accent={accent} last />
      </div>

      {!recording && (
        <div className="mt-3 text-center">
          <button onClick={startRecording} disabled={busy || full} data-testid="smart-intake-voice"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-400 hover:text-white transition-colors disabled:opacity-50">
            <Mic className="w-3.5 h-3.5" /> {t("Lieber sprechen? Sprachnachricht aufnehmen")}
          </button>
        </div>
      )}

      <input ref={fotoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadFile(e.target.files, "foto")} />
      <input ref={shotRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files, "screenshot")} />
      <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadFile(e.target.files, "datei")} />
    </div>
  );
}
